import React, { useState } from "react";
import { MessageSquareText, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import { Card, PageHeader, Button, Badge } from "@/components/common";

export default function WhatsApp() {
  const { branch } = useAuth();
  const [text, setText] = useState("");
  const [draft, setDraft] = useState(null);
  const [usedAi, setUsedAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const parse = async () => {
    if (!text.trim()) return toast.error("Tempel chat WhatsApp dulu");
    setBusy(true);
    try {
      const res = await api.post("/whatsapp/parse", { text, branch_id: branch?.id });
      setDraft(res.draft); setUsedAi(res.used_ai);
      toast.success(res.used_ai ? "Berhasil diproses AI" : "Diproses (mode dasar)");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const matched = (draft?.items || []).filter((i) => i.matched_product_id);

  const createOrder = async () => {
    if (!branch) return toast.error("Pilih cabang dulu");
    if (!matched.length) return toast.error("Tidak ada item yang cocok dengan produk");
    setCreating(true);
    try {
      const body = {
        branch_id: branch.id, order_type: draft.is_preorder ? "preorder" : "whatsapp",
        customer_name: draft.customer_name || "", customer_phone: draft.customer_phone || "",
        delivery_address: draft.delivery_address || "", notes: draft.notes || "",
        due_date: draft.due_date || null,
        items: matched.map((i) => ({ product_id: i.matched_product_id, qty: i.qty || 1, price: i.price || undefined })),
        payments: draft.is_preorder && draft.dp_amount ? [{ method: "cash", amount: draft.dp_amount }] : [],
      };
      const o = await api.post("/orders", body);
      toast.success(`Order ${o.order_no} dibuat`);
      setDraft(null); setText("");
    } catch (e) { toast.error(e.message); } finally { setCreating(false); }
  };

  return (
    <>
      <PageHeader title="WhatsApp → Order" subtitle="Ubah chat pelanggan jadi draft order otomatis" icon={MessageSquareText} />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <label className="text-sm font-medium text-slate-600 mb-2 block">Tempel chat WhatsApp</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} placeholder="Contoh: Halo mau pesan buket mawar merah 12 tangkai untuk besok jam 3 sore, dikirim ke Jl. Melati No 5. DP 150rb ya." className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm outline-none focus:border-primary" data-testid="wa-text" />
          <Button className="w-full mt-3" onClick={parse} disabled={busy} data-testid="wa-parse">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} Proses
          </Button>
        </Card>

        <Card className="p-5">
          {!draft ? (
            <div className="text-center text-slate-400 py-16 text-sm">Draft order akan muncul di sini.</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-800">Draft Order</h3>
                {usedAi ? <Badge className="bg-primary/10 text-primary">AI</Badge> : <Badge className="bg-slate-100 text-slate-500">Dasar</Badge>}
                {draft.is_preorder && <Badge className="bg-amber-100 text-amber-700">Pre-order</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Info label="Pelanggan" value={draft.customer_name} />
                <Info label="Telepon" value={draft.customer_phone} />
                {draft.delivery_address && <div className="col-span-2"><Info label="Alamat" value={draft.delivery_address} /></div>}
                {draft.due_date && <Info label="Tanggal" value={draft.due_date} />}
                {draft.dp_amount && <Info label="DP" value={rupiah(draft.dp_amount)} />}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Item</div>
                <div className="space-y-1.5">
                  {(draft.items || []).map((i, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm border border-slate-100 rounded-xl px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-slate-700 truncate">{i.qty}× {i.matched_product_name || i.query}</div>
                        {!i.matched_product_id && <span className="text-xs text-amber-600">Tidak cocok — abaikan</span>}
                      </div>
                      {i.price != null && <span className="font-medium">{rupiah(i.price)}</span>}
                    </div>
                  ))}
                </div>
              </div>
              {draft.notes && <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-3">{draft.notes}</div>}
              <Button className="w-full" onClick={createOrder} disabled={creating || !matched.length} data-testid="wa-create">
                {creating ? "Membuat..." : `Buat Order (${matched.length} item cocok)`}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

const Info = ({ label, value }) => (
  <div><div className="text-xs text-slate-400">{label}</div><div className="text-slate-700">{value || "-"}</div></div>
);
